import { getLogger, URL } from "@neru/base/utils";
import React, { Component, type ErrorInfo } from "react";
import ReactDOMClient from "react-dom/client";
import singleSpaReact from "single-spa-react";

import Bootstrap from "./Bootstrap";
import { config } from "./utils/config";

interface StoreChangeEventData {
  storeId: string;
}

interface SinglePageAppProps {
  routerBase: string;
  userStore?: {
    $subscribe: (callback: (data: StoreChangeEventData) => void) => void;
    info: MFA.UserInfo;
  };
}

interface SinglePageAppState {
  hasError: boolean;
  userInfo: MFA.UserInfo;
}

const log = getLogger("MFARoot");

const transformForLogging = (userData?: MFA.UserInfo) => {
  if (!userData) {
    return {};
  }

  if (userData.status !== "logged-in") {
    return userData;
  }

  return {
    apiKeys: (userData.apiKeys || []).map((key) => ({
      apiKey: key?.apiKey,
      master: key?.master,
      suspended: key?.suspended,
    })),
    email: userData.email,
    id: userData.nexmoUserId,
    status: userData.status,
  };
};

class SinglePageApp extends Component<SinglePageAppProps, SinglePageAppState> {
  override state: SinglePageAppState = {
    hasError: false,
    userInfo: { status: "pending" },
  };

  constructor(props: SinglePageAppProps) {
    super(props);
    config.setBasePath(props.routerBase);
  }

  handlePropsUpdate = () => {
    if (!this.props.userStore) return;

    log.info(
      `State updated: ${JSON.stringify(transformForLogging(this.props.userStore.info))}`,
    );
    this.setState({ userInfo: this.props.userStore.info });
  };

  override componentDidMount() {
    log.info(
      `State received: ${JSON.stringify(transformForLogging(this.props.userStore?.info))}`,
    );

    if (!this.props.userStore) return;

    this.setState({ userInfo: this.props.userStore.info });

    this.props.userStore.$subscribe(({ storeId }: StoreChangeEventData) => {
      if (storeId === "user") {
        this.handlePropsUpdate();
      }
    });
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(error, info);
    this.setState({ hasError: true });
  }

  override render() {
    if (!URL.isWhitelistedDomain(globalThis.location.hostname)) {
      return (
        <div>
          <h3>Forbidden</h3>
          <p>Please contact support.</p>
        </div>
      );
    }

    if (this.state.hasError) {
      return (
        <div>
          <h3>Something went wrong!</h3>
          <p>Try reloading the page.</p>
        </div>
      );
    }

    return (
      <div className="vvd-root vivid-scope neru">
        <Bootstrap userInfo={this.state.userInfo} />
      </div>
    );
  }
}

const reactLifecycles = singleSpaReact({
  React,
  ReactDOMClient,
  rootComponent: SinglePageApp,
});

export const bootstrap = reactLifecycles.bootstrap;
export const mount = reactLifecycles.mount;
export const unmount = reactLifecycles.unmount;

export default reactLifecycles;
